import { Link, useParams, Navigate } from "react-router-dom";
import { Check, AlertCircle, ArrowUpRight, MessageCircle, ChevronLeft } from "lucide-react";
import { pageBySlug, SERVICE_PAGES } from "@/lib/servicePages";
import { ALL_SERVICES } from "@/lib/catalog";
import { inr, scrollToId } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { ConsultForm } from "@/components/ConsultForm";
import { ConsultNotice } from "@/components/ConsultNotice";
import { waFor } from "@/components/FloatingWhatsApp";
import { FaqList } from "@/sections/Faq";
import { ContactDetails } from "@/sections/ConsultSection";
import { Footer } from "@/sections/Footer";
import { track } from "@/lib/analytics";

const List = ({ title, items, icon: Icon, testId }) => (
  <div className="rounded-3xl border border-white/10 bg-[#0E1526] p-6 sm:p-8" data-testid={testId}>
    <h2 className="font-display text-xl font-bold text-white sm:text-2xl">{title}</h2>
    <ul className="mt-5 space-y-3">
      {items.map((t) => <li key={t} className="flex gap-3 text-sm text-slate-200 sm:text-base"><Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#00F0FF]" />{t}</li>)}
    </ul>
  </div>
);

export default function ServicePage() {
  const { slug } = useParams();
  const p = pageBySlug(slug);
  if (!p) return <Navigate to="/" replace />;
  const prices = p.priceIds.map((id) => ALL_SERVICES.find((s) => s.id === id)).filter(Boolean);
  const related = SERVICE_PAGES.filter((x) => x.slug !== slug).slice(0, 4);

  return (
    <main className="pt-32" data-testid="service-page">
      <Seo title={p.seoTitle} description={p.seoDesc} path={`/services/${p.slug}`} />
      <section className="mx-auto max-w-7xl px-5 pb-16 sm:px-8 lg:px-12">
        <Link to="/" state={{ to: "services" }} className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white" data-testid="service-back-link"><ChevronLeft className="h-4 w-4" />All services</Link>
        <Reveal className="mt-8">
          <Eyebrow>Service</Eyebrow>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="service-page-title">{p.h1}</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="service-page-what">{p.what}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => { track("consultation_click", { service: p.name, location: "service_page_hero" }); scrollToId("consult"); }} data-testid="service-page-consult-button"
              className="group flex items-center gap-2 rounded-full bg-[#00F0FF] px-6 py-3.5 font-semibold text-[#070B14] transition-transform hover:scale-[1.03]">
              Book Free Consultation <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </button>
            <a href={waFor(p.name)} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { location: "service_page", service: p.name })} data-testid="service-page-whatsapp-button"
              className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-white hover:border-[#25D366]">
              <MessageCircle className="h-4 w-4" /> Enquire on WhatsApp
            </a>
          </div>
          <ConsultNotice className="mt-8" testId="service-page-consult-notice" />
        </Reveal>
      </section>

      <section className="mx-auto grid max-w-7xl gap-5 px-5 pb-16 sm:px-8 md:grid-cols-2 lg:px-12">
        <Reveal><List title="How it benefits your business" items={p.benefits} icon={Check} testId="service-benefits" /></Reveal>
        <Reveal delay={0.05}><List title="What's included" items={p.included} icon={Check} testId="service-included" /></Reveal>
        <Reveal delay={0.1} className="md:col-span-2"><List title="Business problems it solves" items={p.problems} icon={AlertCircle} testId="service-problems" /></Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-16 sm:px-8 lg:px-12">
        <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Related services &amp; prices</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="service-prices">
          {prices.map((s) => (
            <div key={s.id} className="flex flex-col rounded-2xl border border-white/10 bg-[#0E1526] p-5">
              <p className="text-sm font-semibold text-white">{s.name}</p>
              <p className="mt-2 font-mono text-xl font-bold text-[#00F0FF]">{inr(s.price)}</p>
              {s.fee && <p className="mt-2 text-xs text-amber-200/90">{s.fee}</p>}
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-400">Third-party charges (domain, hosting, API, WhatsApp platform, CRM, payment gateway) are separate unless confirmed. See all prices on the <Link to="/" state={{ to: "pricing" }} className="text-[#00F0FF] underline">pricing section</Link>.</p>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-16 sm:px-8 lg:px-12">
        <h2 className="mb-6 font-display text-2xl font-bold text-white sm:text-3xl">Frequently asked questions</h2>
        <FaqList faqs={p.faqs} prefix="service-faq" />
      </section>

      <section id="consult" className="mx-auto grid max-w-7xl gap-10 px-5 pb-20 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">Book a free consultation for {p.name}</h2>
          <p className="mt-4 text-slate-300">Share a few details and we'll call you back. No charges for the first consultation.</p>
          <div className="mt-8"><ContactDetails /></div>
        </div>
        <div className="lg:col-span-7"><ConsultForm defaultService={p.consult} source={`service_${p.slug}`} /></div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:px-12">
        <h2 className="font-display text-xl font-bold text-white">Explore other services</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {related.map((r) => <Link key={r.slug} to={`/services/${r.slug}`} className="rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200 hover:border-[#00F0FF]" data-testid={`related-${r.slug}`}>{r.name}</Link>)}
        </div>
      </section>
      <Footer />
    </main>
  );
}
